import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useNavigate } from 'react-router-dom';
import { TopStatusBar } from './TopStatusBar';
import { Sidebar } from './Sidebar';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import { Table } from '../../types/domain';
import { Plus, Table2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

export const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentProject, projects, setCurrentProject, setCurrentTable, refreshTables, canAdmin, canEdit } = useAuth();
  const [createTableOpen, setCreateTableOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [tableName, setTableName] = useState('');
  const [tableSlug, setTableSlug] = useState('');
  const [isSlugManual, setIsSlugManual] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleCloseMobile = useCallback(() => {
    setMobileNavOpen(false);
  }, []);

  const handleToggleMobileNav = useCallback(() => {
    setMobileNavOpen((prev) => !prev);
  }, []);

  const generateKebabSlug = (input: string) => {
    return input
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Global event listeners for API client interceptors & table modal triggers
  useEffect(() => {
    const handlePermissionDenied = (e: any) => {
      const { requiredTier, message } = e.detail || {};
      showToast({
        type: 'error',
        title: 'Action Restricted (HTTP 403)',
        message:
          message ||
          `This operation requires '${requiredTier || 'edit'}' tier. Switch your role in the top header.`,
      });
    };

    const handleQuotaExceeded = (e: any) => {
      const { message } = e.detail || {};
      showToast({
        type: 'warning',
        title: 'Plan Quota Exceeded (HTTP 402)',
        message: message || 'Organization seat or submission quota limit reached. Upgrade required.',
      });
    };

    const handleRateLimited = (e: any) => {
      const { retryAfter, message } = e.detail || {};
      showToast({
        type: 'warning',
        title: 'Rate Limited (HTTP 429)',
        message:
          message || `Too many requests. Please wait ${retryAfter ? retryAfter + 's' : 'a moment'}.`,
      });
    };

    const handleOpenCreateTable = () => {
      setCreateTableOpen(true);
    };

    window.addEventListener('ericius:permission-denied', handlePermissionDenied);
    window.addEventListener('ericius:quota-exceeded', handleQuotaExceeded);
    window.addEventListener('ericius:rate-limited', handleRateLimited);
    window.addEventListener('ericius:open-create-table', handleOpenCreateTable);

    return () => {
      window.removeEventListener('ericius:permission-denied', handlePermissionDenied);
      window.removeEventListener('ericius:quota-exceeded', handleQuotaExceeded);
      window.removeEventListener('ericius:rate-limited', handleRateLimited);
      window.removeEventListener('ericius:open-create-table', handleOpenCreateTable);
    };
  }, [showToast]);

  const handleCreateTable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tableName.trim()) {
      showToast({
        type: 'warning',
        title: 'Table Name Required',
        message: 'Please enter a name for the datastore.',
      });
      return;
    }

    const activeProj = currentProject || projects[0];
    if (!activeProj) {
      showToast({
        type: 'error',
        title: 'No Active Project',
        message: 'Unable to identify active project. Please refresh or create a project first.',
      });
      return;
    }

    if (!currentProject && activeProj) {
      setCurrentProject(activeProj);
    }

    const baseSlug = generateKebabSlug(tableName);
    const slug = tableSlug.trim() || baseSlug || `datastore-${Date.now().toString(36)}`;
    setIsCreating(true);

    try {
      const createdTable = await apiClient<Table>(`/projects/${activeProj.id}/tables`, {
        method: 'POST',
        body: JSON.stringify({
          name: tableName.trim(),
          slug,
          schema_mode: 'dynamic',
          honeypot_field: '_gotcha',
          honeypot_fields: ['_gotcha', '_hp'],
          rate_limit_rpm: 600,
        }),
      });

      showToast({
        type: 'success',
        title: 'Datastore Created',
        message: `Table '${tableName}' is ready for submissions.`,
      });

      setTableName('');
      setTableSlug('');
      setIsSlugManual(false);
      setCreateTableOpen(false);

      // Select active table and navigate directly to the new datastore grid
      setCurrentTable(createdTable);
      await refreshTables();
      navigate(`/tables/${createdTable.id}`);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to create table',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && createTableOpen && !isCreating) {
        setCreateTableOpen(false);
        setTableName('');
        setTableSlug('');
        setIsSlugManual(false);
      }
    };
    if (createTableOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [createTableOpen, isCreating]);

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-cf-bg text-cf-textPrimary">
      {/* Full-Height Top-to-Bottom Sidebar (Responsive Drawer on Mobile) */}
      <Sidebar
        onNewTableModal={(canAdmin || canEdit) ? () => setCreateTableOpen(true) : undefined}
        mobileOpen={mobileNavOpen}
        onCloseMobile={handleCloseMobile}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Interactive Breadcrumbs Header */}
        <TopStatusBar
          onToggleMobileNav={handleToggleMobileNav}
          mobileNavOpen={mobileNavOpen}
        />

        {/* Dynamic Page Outlet */}
        <main className="flex-1 p-3 sm:p-4 md:p-5 overflow-x-hidden overflow-y-auto w-full flex flex-col min-h-0">
          <Outlet />
        </main>
      </div>

      {/* New Table Modal */}
      <Modal
        isOpen={createTableOpen}
        onClose={() => {
          if (!isCreating) {
            setCreateTableOpen(false);
            setTableName('');
            setTableSlug('');
            setIsSlugManual(false);
          }
        }}
        title="Create Dynamic Datastore"
        maxWidth="md"
      >
        <form onSubmit={handleCreateTable} className="space-y-4">
          <Input
            label="Table Name"
            type="text"
            required
            size="sm"
            value={tableName}
            onChange={(e) => {
              const newName = e.target.value;
              setTableName(newName);
              if (!isSlugManual) {
                setTableSlug(
                  newName
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+/, '')
                );
              }
            }}
            onBlur={() => {
              if (!isSlugManual) {
                setTableSlug((prev) => prev.replace(/-+$/, ''));
              }
            }}
            placeholder="e.g. Contact Inquiries"
          />

          <Input
            label="Table Slug (URL / Ingestion Key)"
            type="text"
            size="sm"
            mono
            value={tableSlug}
            onChange={(e) => {
              const val = e.target.value;
              setTableSlug(val);
              setIsSlugManual(val.trim().length > 0);
            }}
            placeholder="e.g. contact-inquiries"
          />

          <div className="p-3 bg-cf-bg rounded border border-cf-border text-xs text-cf-textSecondary">
            💡 <span className="font-semibold text-cf-textPrimary">Dynamic Schema:</span> New fields in incoming JSON or HTML payloads will be automatically discovered and displayed on the data grid.
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-cf-border">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={() => {
                setCreateTableOpen(false);
                setTableName('');
                setTableSlug('');
                setIsSlugManual(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="xs"
              isLoading={isCreating}
              disabled={isCreating || !tableName.trim()}
              leftIcon={<Plus size={13} />}
            >
              Create Table
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
